import React, { memo, useState, useMemo, useCallback } from "react";
import { ReactComponent as ActionRequiredIcon } from "../../assets/actionrequired.svg";
import { ReactComponent as InProgressIcon } from "../../assets/inprogress.svg";

import {
  TabsSectionStyled,
  TabsContainerStyled,
  TabStyled,
  TabIconStyled,
  TabContentStyled,
  TabCountStyled,
  TabLabelStyled,
  DividerStyled,
  TabContentContainerStyled,
  StatusTabsWrapperStyled,
} from "../../styles";
import {
  ActionRequiredListItem,
  ActionRequiredListItemData,
} from "../ActionRequiredListItem/ActionRequiredListItem";
import {
  InProgressVehicleListItem,
  InProgressVehicleListItemData,
} from "../InProgressVehicleListItem/InProgressVehicleListItem";
import { useActionRequiredVehicles } from "../../hooks/useActionRequiredVehicles";
import { useInProgressVehicles } from "../../hooks/useInProgressVehicles";
import { useExitTransition } from "../../hooks/useExitTransition";

// Keep the leaving row mounted long enough to finish its exit animation
// (slightly longer than the 560ms keyframe so it always completes). During the
// ~60ms tail the row is already fully transparent — it's an unmount safety
// margin, not a frozen row.
const IN_PROGRESS_EXIT_MS = 620;

const getInProgressKey = (vehicle: InProgressVehicleListItemData): string =>
  vehicle.id;

// ============================================================================
// Types
// ============================================================================

export type TabType = "actionRequired" | "inProgress";

export interface StatusTabsProps {
  actionRequiredCount?: number;
  inProgressCount?: number;
  actionRequiredVehicles?: ActionRequiredListItemData[];
  inProgressVehicles?: InProgressVehicleListItemData[];
  onTabChange?: (tab: TabType) => void;
}

// ============================================================================
// Business Logic / Hooks
// ============================================================================

/**
 * Hook to manage tab state and selection
 */
const useTabState = (initialTab: TabType = "actionRequired") => {
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const handleTabClick = useCallback(
    (tab: TabType, onTabChange?: (tab: TabType) => void) => {
      setActiveTab(tab);
      onTabChange?.(tab);
    },
    []
  );

  return {
    activeTab,
    handleTabClick,
  };
};

/**
 * Hook to determine which vehicles to display
 * Priority: props > hook data > empty array
 */
const useVehicleData = (
  actionRequiredVehicles?: ActionRequiredListItemData[],
  inProgressVehicles?: InProgressVehicleListItemData[]
) => {
  const actionRequiredFromHook = useActionRequiredVehicles();
  const inProgressFromHook = useInProgressVehicles();

  const actionRequiredToShow = useMemo(() => {
    if (actionRequiredVehicles && actionRequiredVehicles.length > 0) {
      return actionRequiredVehicles;
    }
    return actionRequiredFromHook;
  }, [actionRequiredVehicles, actionRequiredFromHook]);

  const inProgressToShow = useMemo(() => {
    if (inProgressVehicles && inProgressVehicles.length > 0) {
      return inProgressVehicles;
    }
    return inProgressFromHook;
  }, [inProgressVehicles, inProgressFromHook]);

  return {
    actionRequiredToShow,
    inProgressToShow,
  };
};

/**
 * Calculate the display count for each tab
 */
const useTabCounts = (
  actionRequiredToShow: ActionRequiredListItemData[],
  inProgressToShow: InProgressVehicleListItemData[],
  actionRequiredCount?: number,
  inProgressCount?: number
) => {
  return useMemo(
    () => ({
      actualActionRequiredCount:
        actionRequiredCount ?? actionRequiredToShow.length,
      actualInProgressCount: inProgressCount ?? inProgressToShow.length,
    }),
    [
      actionRequiredCount,
      inProgressCount,
      actionRequiredToShow.length,
      inProgressToShow.length,
    ]
  );
};

// ============================================================================
// Presentation Components
// ============================================================================

interface TabButtonProps {
  label: string;
  count: number;
  countColor: string;
  isActive: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  testId: string;
  countTestId: string;
}

const TabButton: React.FC<TabButtonProps> = memo(
  ({
    label,
    count,
    countColor,
    isActive,
    icon,
    onClick,
    testId,
    countTestId,
  }) => {
    return (
      <TabStyled $isActive={isActive} onClick={onClick} data-test-id={testId}>
        <TabIconStyled>{icon}</TabIconStyled>
        <TabContentStyled>
          <TabCountStyled $color={countColor} data-test-id={countTestId}>
            {count}
          </TabCountStyled>
          <TabLabelStyled
            $color={isActive ? "#172A3F" : "#606060"}
            $fontWeight={isActive ? 500 : 300}
          >
            {label}
          </TabLabelStyled>
        </TabContentStyled>
      </TabStyled>
    );
  }
);
TabButton.displayName = "TabButton";

interface TabContentProps<
  T extends ActionRequiredListItemData | InProgressVehicleListItemData
> {
  tabType: TabType;
  activeTab: TabType;
  vehicles: T[];
  renderItem: (vehicle: T, index: number, isLast: boolean) => React.ReactNode;
  testId: string;
}

const TabContent = <
  T extends ActionRequiredListItemData | InProgressVehicleListItemData
>({
  tabType,
  activeTab,
  vehicles,
  renderItem,
  testId,
}: TabContentProps<T>) => {
  if (activeTab !== tabType) {
    return null;
  }

  return (
    <TabContentContainerStyled data-test-id={testId}>
      {vehicles.map((vehicle, index) =>
        renderItem(vehicle, index, index === vehicles.length - 1)
      )}
    </TabContentContainerStyled>
  );
};
TabContent.displayName = "TabContent";

// ============================================================================
// Main Component
// ============================================================================

export const StatusTabs = memo<StatusTabsProps>(
  ({
    actionRequiredCount,
    inProgressCount,
    actionRequiredVehicles,
    inProgressVehicles,
    onTabChange,
  }) => {
    // Business Logic
    const { activeTab, handleTabClick } = useTabState("actionRequired");
    const { actionRequiredToShow, inProgressToShow } = useVehicleData(
      actionRequiredVehicles,
      inProgressVehicles
    );
    const { actualActionRequiredCount, actualInProgressCount } = useTabCounts(
      actionRequiredToShow,
      inProgressToShow,
      actionRequiredCount,
      inProgressCount
    );

    // Keep rows that just left the In Progress list mounted briefly so they can
    // animate out (slide in the vehicle's travel direction + fade). The tab
    // count above still reflects only the live rows.
    const inProgressPresence = useExitTransition(
      inProgressToShow,
      getInProgressKey,
      IN_PROGRESS_EXIT_MS
    );

    // Event Handlers
    const handleActionRequiredClick = useCallback(() => {
      handleTabClick("actionRequired", onTabChange);
    }, [handleTabClick, onTabChange]);

    const handleInProgressClick = useCallback(() => {
      handleTabClick("inProgress", onTabChange);
    }, [handleTabClick, onTabChange]);

    // Render Functions
    const renderActionRequiredItem = useCallback(
      (
        vehicle: ActionRequiredListItemData,
        _index: number,
        isLast: boolean
      ) => (
        <ActionRequiredListItem
          key={vehicle.id}
          vehicle={vehicle}
          isLast={isLast}
        />
      ),
      []
    );

    // Presentation
    return (
      <StatusTabsWrapperStyled data-test-id="status-tabs-wrapper">
        <TabsSectionStyled>
          <TabsContainerStyled>
            <TabButton
              label="Action Required"
              count={actualActionRequiredCount}
              countColor="#3F7F7D"
              isActive={activeTab === "actionRequired"}
              icon={<ActionRequiredIcon width={40} height={40} />}
              onClick={handleActionRequiredClick}
              testId="status-tab-action-required"
              countTestId="status-tab-action-required-count"
            />

            <TabButton
              label="In Progress"
              count={actualInProgressCount}
              countColor="#004ECC"
              isActive={activeTab === "inProgress"}
              icon={<InProgressIcon width={40} height={40} />}
              onClick={handleInProgressClick}
              testId="status-tab-in-progress"
              countTestId="status-tab-in-progress-count"
            />
          </TabsContainerStyled>
          <DividerStyled />
        </TabsSectionStyled>

        <TabContent
          tabType="actionRequired"
          activeTab={activeTab}
          vehicles={actionRequiredToShow}
          renderItem={renderActionRequiredItem}
          testId="status-tab-action-required-content"
        />

        {activeTab === "inProgress" && (
          <TabContentContainerStyled data-test-id="status-tab-in-progress-content">
            {inProgressPresence.map((entry, index) => (
              <InProgressVehicleListItem
                key={entry.key}
                vehicle={entry.item}
                isLast={index === inProgressPresence.length - 1}
                isExiting={entry.isExiting}
              />
            ))}
          </TabContentContainerStyled>
        )}
      </StatusTabsWrapperStyled>
    );
  }
);

StatusTabs.displayName = "StatusTabs";
